// ============================================
// Seed Templates: Compliance & Operations
// Category: compliance-ops
// Templates: 9
// ============================================

import type { DocTemplate } from '../../types/docops';

export const SEED_TEMPLATES_COMPLIANCE: DocTemplate[] = [
  // ── 8.1 Data Operations Specification ──
  {
    id: '8.1-data-ops-spec',
    categoryId: 'compliance-ops',
    track: 'operational',
    title: 'Data Operations Specification',
    description:
      'Written specification of the Data Operations Center already built in the admin dashboard. Covers 8 systems: compliance tracking, data requests, consent management, retention, child safety, breach detection, audit logging, third-party sharing. ALREADY BUILT.',
    templateContent: `# Data Operations Specification

**{{brandEntity}}**

_This document is the written specification of the Data Operations Center already built in the admin dashboard._

## System 1 — Compliance Tracking
{{AI_GENERATED_CONTENT}}

## System 2 — Data Requests
{{AI_GENERATED_CONTENT}}

## System 3 — Consent Management
{{AI_GENERATED_CONTENT}}

## System 4 — Retention
{{AI_GENERATED_CONTENT}}

## System 5 — Child Safety
{{AI_GENERATED_CONTENT}}

## System 6 — Breach Detection
{{AI_GENERATED_CONTENT}}

## System 7 — Audit Logging
{{AI_GENERATED_CONTENT}}

## System 8 — Third-Party Sharing
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: [
      'brandEntity',
    ],
    generationPrompt:
      'Already built as the Data Operations Center in the admin dashboard. Covers 8 systems: compliance tracking, data requests, consent management, retention, child safety, breach detection, audit logging, third-party sharing. This document is the written specification of that system.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 20,
    sortOrder: 1,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.2 Breach Response Plan (Standalone) ──
  {
    id: '8.2-breach-response-plan',
    categoryId: 'compliance-ops',
    title: 'Breach Response Plan (Standalone)',
    description:
      'Expanded standalone breach response plan with phases for detection, containment, investigation, notification, remediation, and post-incident review. Includes contact tree and decision flowchart.',
    templateContent: `# Breach Response Plan

**{{brandEntity}}**

Incident Commander: {{ceoName}}

## Phase 1 — Detection and Initial Assessment
{{AI_GENERATED_CONTENT}}

## Phase 2 — Containment
{{AI_GENERATED_CONTENT}}

## Phase 3 — Investigation
{{AI_GENERATED_CONTENT}}

## Phase 4 — Notification
{{AI_GENERATED_CONTENT}}

## Phase 5 — Remediation
{{AI_GENERATED_CONTENT}}

## Phase 6 — Post-Incident Review
{{AI_GENERATED_CONTENT}}

## Contact Tree
{{AI_GENERATED_CONTENT}}

## Decision Flowchart
{{AI_GENERATED_CONTENT}}

## Template Notification Letters
{{AI_GENERATED_CONTENT}}

Contact: {{ceoName}} (Incident Commander) | {{legalEmail}} | {{privacyEmail}} | {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'legalEmail',
      'privacyEmail',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate standalone Breach Response Plan for {{brandEntity}}. Expanded from HIPAA Breach Notification Policy. Phases: 1) Detection and initial assessment (who to notify internally, 24-hour assessment window), 2) Containment (isolate affected systems, preserve evidence), 3) Investigation (scope assessment, root cause, 4-factor risk assessment), 4) Notification (individual notice within 60 days, HHS, state AG if required, media if >=500 in state), 5) Remediation (fix vulnerability, update controls), 6) Post-incident review (lessons learned, policy updates). Include contact tree, decision flowchart, template notification letters. Contact: {{ceoName}} (Incident Commander), {{legalEmail}}, {{privacyEmail}}. Format: detailed response plan with flowcharts described in text.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 2,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.3 Data Retention Policy (Standalone) ──
  {
    id: '8.3-data-retention-policy',
    categoryId: 'compliance-ops',
    title: 'Data Retention Policy (Standalone)',
    description:
      'Standalone data retention policy with retention periods per data category, automated enforcement via Cloud Functions, and exception process for legal holds.',
    templateContent: `# Data Retention Policy

**{{brandEntity}}**

## Retention Schedule

| Data Category | Retention Period | Action |
|---------------|-----------------|--------|
| Chat transcripts | 365 days | Archive/Delete |
| Inactive user data | 730 days | Anonymize/Delete |
| Session data | 30 days | Delete |
| Export packages | 7 days | Delete |
| Backup data | 90 days | Delete |
| Audit logs | 2190 days (6 years) | Retain (HIPAA) |
| Data request records | 2190 days (6 years) | Retain (HIPAA) |
| Consent records | 2190 days (6 years) | Retain (HIPAA) |

## Automated Enforcement
{{AI_GENERATED_CONTENT}}

## Exception Process — Legal Holds
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate standalone Data Retention Policy for {{brandEntity}}. Retention periods: chat transcripts 365 days, inactive user data 730 days, session data 30 days, export packages 7 days, backup data 90 days, audit logs 2190 days (6 years per HIPAA), data request records 2190 days, consent records 2190 days. Actions per category: archive, anonymize, delete, retain for legal. Automated enforcement via Cloud Functions. Exception process for legal holds. Contact: {{complianceEmail}}. Format: policy document with retention schedule table.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 5,
    sortOrder: 3,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.4 Minor Detection & Protection Policy ──
  {
    id: '8.4-minor-detection-policy',
    categoryId: 'compliance-ops',
    title: 'Minor Detection & Protection Policy',
    description:
      'Policy for detecting and handling minors on the platform including age gate verification, chat content scanning, response protocols, and COPPA compliance.',
    templateContent: `# Minor Detection & Protection Policy

**{{brandEntity}}**

_Platform restricted to users 18+._

## Detection Methods
{{AI_GENERATED_CONTENT}}

## Response Protocol
{{AI_GENERATED_CONTENT}}

## COPPA Compliance
{{AI_GENERATED_CONTENT}}

## Data Deletion Procedures
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}} | {{privacyEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'complianceEmail',
      'privacyEmail',
    ],
    generationPrompt:
      'Generate Minor Detection & Protection Policy for {{brandEntity}}. Platform restricted to users 18+. Detection methods: age gate at signup (DOB verification), chat content scanning for age indicators, age inconsistency detection, parental language detection. Response protocol: flag > review > if confirmed minor: suspend account immediately, delete all data within 24 hours, create deletion audit trail. COPPA compliance: no data collection from users under 13 under any circumstances. Contact: {{complianceEmail}}, {{privacyEmail}}. Format: policy document.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 5,
    sortOrder: 4,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.5 Third-Party Data Sharing Policy ──
  {
    id: '8.5-data-sharing-policy',
    categoryId: 'compliance-ops',
    title: 'Third-Party Data Sharing Policy',
    description:
      'Policy governing all third-party data sharing with authorized recipients, data categories, legal basis, and security requirements. Includes sharing matrix table.',
    templateContent: `# Third-Party Data Sharing Policy

**{{brandEntity}}**

## Authorized Recipients

| Recipient | Relationship | Data Categories | Legal Basis |
|-----------|-------------|----------------|-------------|
| {{pcEntityName}} | Clinical oversight | Clinical data | Covered Entity |
| {{telehealthNetwork}} | Provider consultations | Consultation data | BAA |
| Pharmacy partner | Prescription fulfillment | Prescription data | BAA |
| {{paymentProcessor}} | Payment processing | Payment data | Processor |
| {{cloudProvider}} | Infrastructure | All platform data | BAA |
| Analytics | Usage metrics | Anonymized only | Legitimate interest |

## Sharing Details
{{AI_GENERATED_CONTENT}}

## No Sale of Personal Data
{{AI_GENERATED_CONTENT}}

## User Consent Requirements
{{AI_GENERATED_CONTENT}}

Contact: {{privacyEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'pcEntityName',
      'telehealthNetwork',
      'pharmacyPartner',
      'paymentProcessor',
      'cloudProvider',
      'privacyEmail',
    ],
    generationPrompt:
      'Generate Third-Party Data Sharing Policy for {{brandEntity}}. Authorized recipients: {{pcEntityName}} (clinical oversight — covered entity), {{telehealthNetwork}} (provider consultations — business associate), pharmacy partner (prescription fulfillment — business associate), {{paymentProcessor}} (payment processing — processor), {{cloudProvider}} (infrastructure — business associate), analytics (anonymized only). For each: data categories shared, legal basis (consent, BAA, legal requirement), security requirements, audit rights. No sale of personal data. User consent required for non-essential sharing. Contact: {{privacyEmail}}. Format: policy with sharing matrix table.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 6,
    sortOrder: 5,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.6 Consent Management Policy ──
  {
    id: '8.6-consent-management-policy',
    categoryId: 'compliance-ops',
    title: 'Consent Management Policy',
    description:
      'Policy defining consent purposes, affirmative opt-in model, granular per-purpose controls, withdrawal procedures, and reconsent requirements.',
    templateContent: `# Consent Management Policy

**{{brandEntity}}**

## Consent Purposes

| Purpose | Required/Optional | Default |
|---------|-------------------|---------|
| Lab data collection | Required | Opt-in |
| Health profile processing | Required | Opt-in |
| Supplement recommendations | Optional | Opt-in |
| Provider sharing | Optional | Opt-in |
| Pharmacy sharing | Optional | Opt-in |
| Marketing email | Optional | Opt-in |
| Usage analytics | Optional | Opt-in |

## Consent Model
{{AI_GENERATED_CONTENT}}

## Withdrawal Procedures
{{AI_GENERATED_CONTENT}}

## Consent Records
{{AI_GENERATED_CONTENT}}

## Reconsent Requirements
{{AI_GENERATED_CONTENT}}

Contact: {{privacyEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'privacyEmail',
    ],
    generationPrompt:
      'Generate Consent Management Policy for {{brandEntity}}. Consent purposes: lab data collection (required), health profile processing (required), supplement recommendations (optional), provider sharing (optional), pharmacy sharing (optional), marketing email (optional), usage analytics (optional). Model: affirmative opt-in for all purposes, granular per-purpose, withdrawal available at any time via /settings/privacy, withdrawal effective immediately, consent records maintained with version/method/timestamp. Reconsent required when policy version changes. Contact: {{privacyEmail}}. Format: policy document.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 4,
    sortOrder: 6,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.7 Incident Response Runbook ──
  {
    id: '8.7-incident-response-runbook',
    categoryId: 'compliance-ops',
    title: 'Incident Response Runbook',
    description:
      'Step-by-step procedures for 8 incident types including data breach, system outage, unauthorized access, minor detection, consent withdrawal, provider complaint, regulatory inquiry, and vendor breach.',
    templateContent: `# Incident Response Runbook

**{{brandEntity}}**

Incident Commander: {{ceoName}}

## Incident Type 1 — Data Breach (HIPAA)
{{AI_GENERATED_CONTENT}}

## Incident Type 2 — System Outage (Firebase/Cloud Functions)
{{AI_GENERATED_CONTENT}}

## Incident Type 3 — Unauthorized Access Detection
{{AI_GENERATED_CONTENT}}

## Incident Type 4 — Minor Detected on Platform
{{AI_GENERATED_CONTENT}}

## Incident Type 5 — Consent Withdrawal with Active Orders
{{AI_GENERATED_CONTENT}}

## Incident Type 6 — Provider Complaint
{{AI_GENERATED_CONTENT}}

## Incident Type 7 — Regulatory Inquiry or Audit
{{AI_GENERATED_CONTENT}}

## Incident Type 8 — Third-Party Vendor Breach Notification Received
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate Incident Response Runbook for {{brandEntity}}. Step-by-step procedures for: 1) Data breach (HIPAA), 2) System outage (Firebase/Cloud Functions), 3) Unauthorized access detection, 4) Minor detected on platform, 5) Consent withdrawal with active orders, 6) Provider complaint, 7) Regulatory inquiry or audit, 8) Third-party vendor breach notification received. Each incident type: severity classification, immediate actions, escalation path, communication templates, resolution steps, post-incident review. Incident Commander: {{ceoName}}. Contact: {{complianceEmail}}. Format: runbook with numbered steps per incident type.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 8,
    sortOrder: 7,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.8 Business Continuity / Disaster Recovery Plan ──
  {
    id: '8.8-business-continuity-dr',
    categoryId: 'compliance-ops',
    title: 'Business Continuity / Disaster Recovery Plan',
    description:
      'HIPAA-required BC/DR plan covering recovery objectives (RPO 1 hour, RTO 4 hours), outage scenarios for all platform systems, and annual testing requirements.',
    templateContent: `# Business Continuity / Disaster Recovery Plan

**{{brandEntity}}**

## Systems Inventory
- Firebase Firestore (primary data)
- Cloud Functions (API layer)
- Firebase Hosting (web app)
- Claude API (AI analysis)
- OpenLoop API (telehealth)
- Stripe (payments)

## Recovery Objectives
- **RPO:** 1 hour (Firestore automatic)
- **RTO:** 4 hours

## Scenario 1 — {{cloudProvider}} Regional Outage
{{AI_GENERATED_CONTENT}}

## Scenario 2 — AI Provider (Anthropic) Outage
{{AI_GENERATED_CONTENT}}

## Scenario 3 — Telehealth Network Outage
{{AI_GENERATED_CONTENT}}

## Scenario 4 — Payment Processor Outage
{{AI_GENERATED_CONTENT}}

## Scenario 5 — Complete Infrastructure Loss
{{AI_GENERATED_CONTENT}}

## Backup Strategy
{{AI_GENERATED_CONTENT}}

## Annual Testing Requirement
{{AI_GENERATED_CONTENT}}

Contact: {{ceoName}} | {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'cloudProvider',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate Business Continuity and Disaster Recovery Plan for {{brandEntity}}. HIPAA-required. Systems: Firebase Firestore (primary data), Cloud Functions (API layer), Firebase Hosting (web app), Claude API (AI analysis), OpenLoop API (telehealth), Stripe (payments). Recovery objectives: RPO 1 hour (Firestore automatic), RTO 4 hours. Scenarios: 1) {{cloudProvider}} regional outage, 2) API provider (Anthropic) outage, 3) Telehealth network outage, 4) Payment processor outage, 5) Complete infrastructure loss. For each: impact assessment, recovery procedure, communication plan, degraded mode of operation. Backup strategy: Firestore automatic backups, code in GitHub, configuration documented. Annual testing requirement. Contact: {{ceoName}}, {{complianceEmail}}. Format: BC/DR plan document.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 8,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 8.9 Compliance Training Schedule ──
  {
    id: '8.9-compliance-training-schedule',
    categoryId: 'compliance-ops',
    title: 'Compliance Training Schedule',
    description:
      'Annual compliance training matrix covering HIPAA, breach reporting, data handling, privacy law awareness, incident response, and vendor management.',
    templateContent: `# Compliance Training Schedule

**{{brandEntity}}**

## Training Matrix

| Topic | Audience | Frequency | Last Completed | Next Due | Delivery Method |
|-------|----------|-----------|----------------|----------|-----------------|
| HIPAA Privacy and Security | All workforce | Annually | | | Online module |
| Breach reporting procedures | All workforce | Annually | | | Live session |
| Platform data handling procedures | New hires | At onboarding | | | Self-paced |
| MHMDA/state privacy law awareness | All workforce | Annually | | | Online module |
| Incident response procedures | Key personnel | Semi-annually | | | Live session |
| Vendor management and BAA obligations | Ops team | Annually | | | Self-paced |

## Training Details
{{AI_GENERATED_CONTENT}}

Contact: {{complianceEmail}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'complianceEmail',
    ],
    generationPrompt:
      'Generate annual Compliance Training Schedule for {{brandEntity}}. Training topics: 1) HIPAA Privacy and Security (all workforce, annually), 2) Breach reporting procedures (all workforce, annually), 3) Platform data handling procedures (new hires, at onboarding), 4) MHMDA/state privacy law awareness (all workforce, annually), 5) Incident response procedures (key personnel, semi-annually), 6) Vendor management and BAA obligations (ops team, annually). Track: topic, audience, frequency, last completed, next due, delivery method (online module, live session, self-paced document review). Contact: {{complianceEmail}}. Format: training matrix table.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 9,
    generationRound: 6,
    dependencies: [],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
